import { i18n } from '../i18n.js';
import { MoonfallCloud } from '../convex-client.js';
/**
 * soundtrack.js
 * The music audio channel — fully independent from sound effects.
 *
 * The slider value IS the actual music playback level (no master, no hidden
 * mix): music plays at `volume` (default 70%), silenced when muted. Preferences
 * persist under moonfall_music_volume / moonfall_music_muted. The volume UI
 * itself lives in audio-settings.js; this module only owns music playback and
 * broadcasts `moonfall:music-changed` for the UI to reflect.
 *
 * MUTED MEANS NO MUSIC ON THE WIRE. A mute (or a volume dragged to 0) holds
 * the track: its place is kept and the element loops half a second of silence
 * (SILENCE_SOURCE, 8 KB, fetched once per device) until the unmute resumes
 * the same track there, inside the press. It kept playing the track at gain 0
 * instead, so a muted player on 1 Mbps downloaded 2.3 MB of music in 40 s
 * beside the screen they were looking at (28 September 2026). The element
 * keeps PLAYING on purpose: see setPlayerVolume for what a silent element
 * would cost the sound effects on an iPhone.
 */


import { AudioGainBus } from './audio-gain-bus.js';
/* Dev-only, and inert unless `?audioTrace=1` on a non-production build. Imported
   HERE so it is evaluated before this module constructs its first `Audio`,
   which is the element it exists to describe. */
import './audio-trace.js';
import { AudioOwnership } from './audio-ownership.js';
let Soundtrack = {};

/* Every document owns its own engine. The Moon Foundry once ran in a
   same-origin frame and adopted the parent's engine here; that frame is gone,
   and a foreign parent (an itch.io embed) is not a place to take audio from. */
Soundtrack = (function () {
        const PLAYBACK_STATE_KEY = 'moonfall_soundtrack_resume';
        const PLAYBACK_STATE_TTL_MS = 2 * 60 * 1000;
        const MUSIC_VOLUME_KEY = 'moonfall_music_volume';
        const MUSIC_MUTED_KEY = 'moonfall_music_muted';
        // Retired 2026-07-11 master slider. Music played at master × 0.7, so we
        // migrate that product to preserve perceived loudness. The master key is
        // removed by sound-effects.js (which loads after us and also needs it).
        const LEGACY_MASTER_KEY = 'moonfall_master_volume';
        const LEGACY_MASTER_MUSIC_MIX = 0.7;
        const LEGACY_MUSIC_MUTE_KEY = 'moonfall_soundtrack_muted';
        const DEFAULT_VOLUME = 0.7;
        const RESTORE_VOLUME = 0.7; // unmuting from a 0 level lands here
        /* What a held track's element plays: half a second of silence (16-bit
           mono PCM, 8 KB), from this origin because the page's CSP admits no
           data: or blob: media, and versioned so a device fetches it once. */
        const SILENCE_SOURCE = '/assets/audio/silence.wav?v=1';
        const DUCK_FADE_MS = 300;
        const PLAYBACK_RETRY_DELAY_MS = 1000;
        const TRACK_RETRY_LIMIT = 1;

        let audioPlayer = null;
        // The last loudness this module COMMANDED. Where iOS ignores
        // `.volume`, reading the element back would lie; this does not.
        let lastCommandedVolume = DEFAULT_VOLUME;
        let playlist = [];
        let currentTrackIndex = 0;
        let volume = DEFAULT_VOLUME; // raw music level = actual playback level
        let musicMuted = false;
        let lastNonZeroVolume = DEFAULT_VOLUME;
        let isInitialized = false;
        let usingConvexPlaylist = false;
        let duckTimer = null;
        let fadeTimer = null;
        let duckActive = false;
        let duckTargetVolume = null;
        let duckFactor = null;
        let pendingResumeState = null;
        let activePlaybackAttempt = null;
        let mediaErrorListener = null;
        let pendingTrackTimer = null;
        let pendingTrackIntent = null;
        const trackFailureCounts = new Map();
        const failedTracks = new Set();
        /* True while playback is paused because the PAGE went away (hidden tab,
           pagehide) rather than because the player stopped it — the only state
           the visibility handler is allowed to resume from. */
        let suspendedByHide = false;
        /* Whether this page WANTS music playing right now, which is not the
           same question as whether the element happens to be playing.
           `paused` cannot answer it at the moment a page is hidden, because
           locking a phone is an audio-session interruption: WebKit (and Chrome
           on Android, on audio-focus loss) pauses the element ITSELF and the
           page is told it is hidden afterwards. Reading `paused` there
           measures which of the two won a race, so the handlers below latched
           the suspend on one ordering and silently declined on the other — and
           on the declining ordering nothing called tryToPlay(), so nothing
           reached the NotAllowedError branch that arms the interaction
           fallback, and the music was dead for the rest of the session while
           SFX (which re-warm unconditionally) kept working. Reported exactly
           that way: "sfx still works after reopening screen but music is dead
           after it". Intent is ours to know, so we record it instead of
           inferring it. */
        let playbackIntended = false;
        /* How often the health watch compares intent against reality. Long
           enough to be invisible on a battery, short enough that a player does
           not sit through a silent enemy turn. */
        const HEALTH_INTERVAL_MS = 4000;
        /* A repair may not stampede: one gesture can deliver pointerdown,
           mousedown, touchend and click, and the watch may tick in the middle
           of them. */
        const REPAIR_COOLDOWN_MS = 600;
        /* After every playable track has failed there is no next track to move
           to. Rather than going silent for the rest of the page (which is what
           it used to do) the failures are forgiven once and the playlist is
           tried again from the top. */
        const PLAYLIST_RETRY_DELAY_MS = 15000;
        const PLAYLIST_RETRY_MAX_DELAY_MS = 300000;
        const PLAYLIST_LOAD_RETRY_DELAY_MS = 5000;
        const PLAYLIST_LOAD_RETRY_LIMIT = 5;
        /* How long a track that says it is playing may make no progress
           WITH NOTHING ARRIVING before it is treated as dead. Generous: a
           phone changing cells legitimately stalls for several seconds and
           recovers by itself. A track whose bytes are still coming in is slow,
           not dead, and is never skipped for it (onElementStalled). */
        const STALL_CONFIRM_MS = 8000;
        let healthTimer = null;
        let playlistRetryTimer = null;
        let playlistRetryAttempts = 0;
        let playlistLoadRetries = 0;
        let stallWatchTimer = null;
        let stallWatchFromTime = null;
        let stallWatchWindowStart = 0;
        let stallWatchBufferedEnd = 0;
        /* When the element last said bytes of its current source arrived
           (`progress`), or 0 before any did. */
        let lastBytesAt = 0;
        let lastRepairAt = 0;
        /* The track a mute is holding (see the header): while true the element
           plays the silence, and `heldTime` is where the track resumes. */
        let holdingTrack = false;
        let heldTime = 0;
        /* The silence could not play in this engine: a mute then holds the
           track with nothing playing at all. */
        let silenceFailed = false;
        /**
         * THE GRAPH'S CLOCK, READ ON THE WAY OUT.
         *
         * `AudioContext.currentTime` advances only while the graph renders, so
         * comparing how far it moved while the page was hidden against how
         * long the page was hidden says whether the graph stopped in between —
         * with no help from the platform. That matters because a screen lock
         * freezes the page: the `statechange` pair for an interruption that
         * began AND ended during the freeze is delivered afterwards, both
         * events reading `running`, so nothing that listens for the context
         * leaving `running` ever sees it leave. A graph that stopped may have
         * lost the node the music is routed through (WebKit promises nothing
         * about it), and a dead node plays as silence while `paused === false`
         * and the context says `running` — which is the report, word for word.
         */
        const GRAPH_CLOCK_GAP_MS = 1500;
        let graphClockAtHide = null;
        /* True only while THIS module is deliberately stopping the element, so
           the health watch does not read its own teardown as a defect. */
        let releasingOwnMedia = false;
        /* Every track has failed. Latched so the health watch does not
           hammer a playlist that is currently unplayable, and cleared by the
           forgiving retry below. */
        let playlistExhausted = false;

        function clamp01(x) {
            const n = Number(x);
            if (!Number.isFinite(n)) return null;
            return Math.max(0, Math.min(1, n));
        }

        function readStored(key) {
            try { return localStorage.getItem(key); } catch (_) { return null; }
        }

        function muted() {
            return musicMuted || volume <= 0;
        }

        /** Actual music playback level, 0 when muted or at a 0 level. */
        function musicVolume() {
            return muted() ? 0 : volume;
        }

        function persistVolume() {
            try { localStorage.setItem(MUSIC_VOLUME_KEY, String(volume)); } catch (_) { }
        }

        function persistMuted() {
            try { localStorage.setItem(MUSIC_MUTED_KEY, String(musicMuted)); } catch (_) { }
        }

        function loadVolumePreferences() {
            const storedVolume = readStored(MUSIC_VOLUME_KEY);
            const storedMuted = readStored(MUSIC_MUTED_KEY);
            const legacyMaster = readStored(LEGACY_MASTER_KEY);
            const legacyMute = readStored(LEGACY_MUSIC_MUTE_KEY);

            if (storedVolume !== null) {
                const n = clamp01(storedVolume);
                volume = n === null ? DEFAULT_VOLUME : n;
            } else if (legacyMaster !== null) {
                // Old music loudness was master × 0.7; that product is the new level.
                const n = clamp01(legacyMaster);
                volume = n === null ? DEFAULT_VOLUME : clamp01(n * LEGACY_MASTER_MUSIC_MIX);
            } else {
                volume = DEFAULT_VOLUME;
            }

            musicMuted = storedMuted !== null ? storedMuted === 'true' : legacyMute === 'true';
            if (volume > 0) lastNonZeroVolume = volume;

            try { localStorage.removeItem(LEGACY_MUSIC_MUTE_KEY); } catch (_) { }

            persistVolume();
            persistMuted();
        }

        /**
         * The single write point for playback loudness.
         *
         * ONE AUTHORITY AT A TIME. On iOS every engine is WebKit and
         * `.volume` is READ-ONLY there — the assignment is swallowed, which is
         * why the whole audio panel did nothing on a phone. Where WebAudio
         * exists the gain node carries the level and `.volume` is pinned to 1,
         * so the two attenuators can never multiply (0.35 x 0.35) and no
         * capability probe stands between the slider and the loudness. Where
         * WebAudio does not exist, `.volume` carries it exactly as before.
         */
        function setPlayerVolume(value) {
            if (!audioPlayer) return;
            const level = Math.max(0, Math.min(1, Number(value) || 0));
            lastCommandedVolume = level;
            const carried = AudioGainBus?.setElementGain?.(audioPlayer, level) === true;
            audioPlayer.volume = carried ? 1 : level;
            /* `.muted` is the belt for the braces `.volume` cannot hold on iOS
               — the assignment above is swallowed there, `.muted` is honoured
               ("the volume button was on mute... why was the music playing?")
               — but ONLY while the graph is not carrying the element.

               A CARRIED ELEMENT IS MUTED BY ITS GAIN ALONE. WebKit's
               media-session manager keeps the iOS audio session in the
               MediaPlayback category only while an audio element that can
               produce audio is playing, and a muted element cannot. Set
               `.muted` here and the page is left with WebAudio alone, which
               is handed the Ambient category — the one that honours the
               ringer/silent switch — so every sound effect goes silent on a
               phone whose switch is on silent, which is most phones. Reported
               exactly that way: "all sound is stopped if I stop music from
               settings. SFX sounds are unrelated." Gain 0 is a complete
               silence and leaves the element audible in WebKit's eyes, so the
               SFX keep the session the music was holding. The bus applies the
               commanded gain BEFORE the handover call-back reaches here, so
               releasing `.muted` on a pending element that just got carried
               has no blip. */
            audioPlayer.muted = !carried && level <= 0;
        }

        function applyVolumeToPlayer() {
            if (!audioPlayer) return;
            // A running fade drives toward a target captured in its closure;
            // kill it so a late tick cannot overwrite this authoritative set.
            if (fadeTimer) {
                clearInterval(fadeTimer);
                fadeTimer = null;
            }
            if (muted()) {
                setPlayerVolume(0);
            } else if (duckActive && typeof duckTargetVolume === 'number') {
                setPlayerVolume(duckTargetVolume);
            } else {
                setPlayerVolume(musicVolume());
            }
        }

        function dispatchMusicChanged() {
            document.dispatchEvent(new CustomEvent('moonfall:music-changed', {
                detail: { volume, muted: muted(), effective: musicVolume() }
            }));
        }

        function setMusicVolumeInternal(value, options = {}) {
            const { persist = true, dispatch = true } = options;
            const n = clamp01(value);
            if (n === null) return;
            volume = n;
            if (n > 0) {
                musicMuted = false;
                lastNonZeroVolume = n;
            } else {
                musicMuted = true;
            }

            if (duckActive && typeof duckFactor === 'number') {
                duckTargetVolume = musicVolume() * duckFactor;
            }
            if (!followMute()) applyVolumeToPlayer();

            // Persist BOTH: the slider itself can flip the mute flag (raising
            // from 0 unmutes, dragging to 0 mutes), so the flag must be saved
            // too or a later reload silently restores a stale mute state.
            if (persist) {
                persistVolume();
                persistMuted();
            }
            if (dispatch) dispatchMusicChanged();
        }

        function toggleMute() {
            if (muted()) {
                musicMuted = false;
                if (volume <= 0) {
                    volume = lastNonZeroVolume > 0 ? lastNonZeroVolume : RESTORE_VOLUME;
                    persistVolume();
                }
            } else {
                musicMuted = true;
            }
            persistMuted();

            if (duckActive && typeof duckFactor === 'number') {
                duckTargetVolume = musicVolume() * duckFactor;
            }
            if (!followMute()) applyVolumeToPlayer();
            dispatchMusicChanged();
            return muted();
        }

        /**
         * A mute holds the track and an unmute gives it back where it was
         * (see the header). Both go through startPlayback, which picks the
         * source from muted() and, once the page has had a gesture, plays it
         * at once: an unmute press resumes the music inside that press, where
         * iOS lets it play. True when it restarted the element, which applied
         * the volume itself.
         */
        function followMute() {
            if (!audioPlayer || playlist.length === 0 || muted() === holdingTrack) return false;
            /* A released element (the page left, or another page took the
               audio) holds nothing to swap, and a mute must not take the audio
               back: the next start picks the source, and the place it stored.
               One the mute itself emptied (no silence in this engine) is
               restarted by the unmute like any other. */
            if (!mediaSourceOf(audioPlayer) && !(holdingTrack && silenceFailed)) {
                holdingTrack = muted();
                return false;
            }
            const time = holdingTrack ? heldTime : Number(audioPlayer.currentTime) || 0;
            /* Inside the player's own press: a swap that throws must not take
               the mute with it (the level still applies, and the panel still
               hears about it). */
            try {
                startPlayback({ src: playlist[currentTrackIndex], currentTime: time });
                return true;
            } catch (error) {
                console.warn('[Soundtrack] Could not swap the held track:', error);
                return false;
            }
        }

        function resumeTimeOf(resumeState, src) {
            if (!resumeState || resumeState.src !== src) return 0;
            const time = Number(resumeState.currentTime);
            return Number.isFinite(time) && time > 0 ? time : 0;
        }

        /**
         * ONE FAILED QUERY IS NOT A SESSION WITHOUT MUSIC.
         *
         * The playlist came from a single Convex read at boot, taken once, with
         * no retry: a phone that was on a lift, on a train, or simply between
         * cells at the wrong second got `[Soundtrack] Failed to load` in the
         * console and silence for as long as the page stayed open. Nothing in
         * the module could recover, because every other path starts from a
         * playlist that was never populated.
         */
        function schedulePlaylistReload() {
            if (playlist.length > 0) return;
            if (playlistLoadRetries >= PLAYLIST_LOAD_RETRY_LIMIT) return;
            const delay = Math.min(
                PLAYLIST_LOAD_RETRY_DELAY_MS * Math.pow(2, playlistLoadRetries),
                PLAYLIST_RETRY_MAX_DELAY_MS,
            );
            playlistLoadRetries += 1;
            setTimeout(() => {
                if (playlist.length > 0) return;
                loadPlaylist().catch(() => schedulePlaylistReload());
            }, delay);
        }

        async function loadPlaylist() {
            if (window.CLIENT_DEBUG_MODE) console.log('Loading soundtrack playlist...');
            let resumeState = null;

            if (!MoonfallCloud || !MoonfallCloud.sounds || !MoonfallCloud.isReady || !MoonfallCloud.isReady()) {
                console.warn('[Soundtrack] Convex not ready, cannot load playlist');
                schedulePlaylistReload();
                return;
            }

            try {
                const tracks = await MoonfallCloud.sounds.listByCategory('soundtrack');
                const availableTracks = (tracks || [])
                    .map(track => track.url)
                    .filter(Boolean);
                resumeState = getApplicableResumeState(availableTracks);
                playlist = resumeState
                    ? buildResumePlaylist(availableTracks, resumeState)
                    : availableTracks;
                cancelPendingTrackChange();
                invalidatePlaybackAttempt();
                trackFailureCounts.clear();
                failedTracks.clear();
                usingConvexPlaylist = playlist.length > 0;
            } catch (error) {
                console.warn('[Soundtrack] Failed to load Convex soundtrack:', error);
                schedulePlaylistReload();
                return;
            }

            if (window.CLIENT_DEBUG_MODE) console.log('Loaded playlist:', playlist);

            if (playlist.length > 0) {
                if (resumeState) {
                    currentTrackIndex = playlist.indexOf(resumeState.src);
                    activatePlaybackHandoff();
                } else {
                    currentTrackIndex = 0;
                    shufflePlaylist();
                }
                startPlayback(resumeState);
                pendingResumeState = null;
            } else {
                console.warn('[Soundtrack] No audio tracks found in Convex');
                showNoSoundtracksMessage();
            }
        }

        function shufflePlaylist() {
            for (let i = playlist.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [playlist[i], playlist[j]] = [playlist[j], playlist[i]];
            }
        }

        function cancelPendingTrackChange({ preserveIntent = false } = {}) {
            if (pendingTrackTimer !== null) {
                clearTimeout(pendingTrackTimer);
                pendingTrackTimer = null;
            }
            if (!preserveIntent) pendingTrackIntent = null;
        }

        function invalidatePlaybackAttempt() {
            if (audioPlayer && mediaErrorListener) {
                audioPlayer.removeEventListener('error', mediaErrorListener);
            }
            mediaErrorListener = null;
            activePlaybackAttempt = null;
        }

        function beginPlaybackAttempt(src) {
            invalidatePlaybackAttempt();
            const attempt = {
                src,
                // The silence a mute holds the track with: its failure is not the track's.
                silence: holdingTrack,
                failureHandled: false,
            };
            activePlaybackAttempt = attempt;
            mediaErrorListener = () => {
                handlePlaybackFailure(attempt, audioPlayer?.error || null);
            };
            audioPlayer.addEventListener('error', mediaErrorListener, { once: true });
            return attempt;
        }

        function startPlayback(resumeState = null) {
            if (playlist.length === 0) return;

            // A late playlist response, retry, or manual advance must never
            // create/play media after the page has moved to the background.
            // Remember the intent and let the visible pageshow path claim and
            // start it instead.
            if (document.hidden) {
                suspendedByHide = true;
                return;
            }

            if (!audioPlayer) {
                audioPlayer = new Audio();
                // Must precede the first src assignment: the bus needs anonymous
                // CORS on the element, and a tainted graph plays as SILENCE.
                // It may decline for now — a context that cannot run yet would
                // make a graph-routed element inaudible — and call back when it
                // takes over, which is what onCarryChanged is registered for.
                AudioGainBus?.attachElement?.(audioPlayer);
                audioPlayer.addEventListener('ended', playNextTrack);
                audioPlayer.addEventListener('pause', onElementPaused);
                audioPlayer.addEventListener('waiting', onElementStalled);
                audioPlayer.addEventListener('stalled', onElementStalled);
                audioPlayer.addEventListener('progress', onElementBytes);
                audioPlayer.addEventListener('playing', onElementProgress);
                audioPlayer.addEventListener('timeupdate', onElementProgress);
            }

            // Starting playback makes this page the audio owner; any other
            // Moonfall page still holding a track releases it now. A HIDDEN
            // page never claims — a background error-retry or track advance
            // must not silence the page the player is actually looking at. If
            // the audio was taken while this page was hidden, it stays silent;
            // the visibility handler reclaims when the player returns.
            AudioOwnership?.claim?.();

            const src = playlist[currentTrackIndex];
            const interacted = document.documentElement.hasAttribute('data-user-interacted');
            // A new source: the last one's bytes and stall say nothing about it.
            cancelStallWatch();
            lastBytesAt = 0;
            /* The source follows the mute (see the header): the track, or the
               silence while the track waits at `heldTime`. */
            holdingTrack = muted();
            if (holdingTrack) {
                heldTime = resumeTimeOf(resumeState, src);
                if (silenceFailed) {
                    releasePlayerMedia();
                    return;
                }
            }
            const attempt = beginPlaybackAttempt(src);
            if (holdingTrack) {
                audioPlayer.loop = true;
                audioPlayer.preload = 'auto';
                audioPlayer.src = SILENCE_SOURCE;
                applyVolumeToPlayer();
            } else {
                audioPlayer.loop = false;
                /* NOTHING IS FETCHED BEFORE IT CAN BE HEARD. Until the page has
                   a gesture the browser will not play this track, yet with the
                   default preload the element downloaded all of it during the
                   boot (measured 25 September 2026 on the phone seat: 3.4 MB
                   between 2.8 and 6.0 s, most of a 4G link while the lobby was
                   still arriving). The first gesture's play() loads it; a page
                   that arrives already interacted loads at once. Set before the
                   source, which starts the element's load. */
                audioPlayer.preload = interacted ? 'auto' : 'none';
                audioPlayer.src = src;
                applyVolumeToPlayer();
                restorePlaybackPosition(resumeState);
            }

            if (interacted) {
                tryToPlay(attempt);
            } else if (window.CLIENT_DEBUG_MODE) {
                console.log('Waiting for user interaction before playing soundtrack');
            }
        }

        function getStoredPlaybackState() {
            try {
                const raw = sessionStorage.getItem(PLAYBACK_STATE_KEY);
                if (!raw) return null;

                const state = JSON.parse(raw);
                const savedAt = Number(state?.savedAt);
                if (!Number.isFinite(savedAt) || Date.now() - savedAt > PLAYBACK_STATE_TTL_MS) {
                    sessionStorage.removeItem(PLAYBACK_STATE_KEY);
                    return null;
                }

                if (!state?.src || !Array.isArray(state.playlist)) return null;
                return state;
            } catch (err) {
                try {
                    sessionStorage.removeItem(PLAYBACK_STATE_KEY);
                } catch (_) { }
                return null;
            }
        }

        function getApplicableResumeState(availableTracks) {
            if (!pendingResumeState || !pendingResumeState.wasPlaying) return null;
            if (!availableTracks.includes(pendingResumeState.src)) return null;
            return pendingResumeState;
        }

        function buildResumePlaylist(availableTracks, resumeState) {
            const available = new Set(availableTracks);
            const ordered = resumeState.playlist.filter(src => available.has(src));
            const orderedSet = new Set(ordered);
            return ordered.concat(availableTracks.filter(src => !orderedSet.has(src)));
        }

        function restorePlaybackPosition(resumeState) {
            if (!audioPlayer || !resumeState || resumeState.src !== playlist[currentTrackIndex]) return;
            const resumeTime = Number(resumeState.currentTime);
            if (!Number.isFinite(resumeTime) || resumeTime <= 0) return;

            const applyCurrentTime = () => {
                try {
                    const duration = Number(audioPlayer.duration);
                    const maxTime = Number.isFinite(duration) && duration > 0
                        ? Math.max(0, duration - 0.25)
                        : resumeTime;
                    audioPlayer.currentTime = Math.max(0, Math.min(resumeTime, maxTime));
                } catch (err) {
                    console.warn('[Soundtrack] Failed to restore playback position:', err);
                }
            };

            if (Number(audioPlayer.readyState) > 0) {
                applyCurrentTime();
            } else {
                audioPlayer.addEventListener('loadedmetadata', applyCurrentTime, { once: true });
            }
        }

        function persistPlaybackState() {
            if (!audioPlayer || playlist.length === 0) return false;

            const src = playlist[currentTrackIndex];
            /* INTENT, NOT THE ELEMENT — the same lesson `playbackIntended`
               exists for. This used to read `!audioPlayer.paused`, so on the
               ordering a phone actually produces (the platform pauses the
               element BEFORE anything else happens) there was never anything to
               save, and every interruption repair restarted the track from
               zero. The player heard the music come back at the beginning of a
               track they were three minutes into. */
            const wasPlaying = playbackIntended && !audioPlayer.ended;
            if (!src || !wasPlaying) return false;

            try {
                sessionStorage.setItem(PLAYBACK_STATE_KEY, JSON.stringify({
                    src,
                    trackIndex: currentTrackIndex,
                    // A held track's place, not the silence's.
                    currentTime: holdingTrack ? heldTime : Number(audioPlayer.currentTime) || 0,
                    playlist: playlist.slice(),
                    muted: muted(),
                    volume,
                    wasPlaying,
                    savedAt: Date.now()
                }));
                return true;
            } catch (err) {
                console.warn('[Soundtrack] Failed to save playback handoff state:', err);
                return false;
            }
        }

        function tryToPlay(attempt = activePlaybackAttempt) {
            if (!audioPlayer || !attempt || attempt !== activePlaybackAttempt || attempt.failureHandled) return;
            // From here on this page wants sound, whatever the element or the
            // platform does with it next.
            playbackIntended = true;
            // A suspended AudioContext outputs silence, and every call site here
            // is already inside (or immediately after) a user gesture — the only
            // moment the platform will let it start.
            AudioGainBus?.resume?.();
            let playPromise;
            try {
                playPromise = audioPlayer.play();
            } catch (error) {
                handlePlaybackFailure(attempt, error);
                return;
            }

            if (playPromise !== undefined) {
                const playbackStarted = () => {
                    // Healthy again: nothing left for the watch to do.
                    if (!musicLooksDead()) stopHealthWatch();
                    /* DELIBERATELY NOT forgiving this track's failure count. A
                       source that starts playing and then fails late would
                       regain its retry every time it started, which is an
                       unbounded loop on a track that always dies thirty seconds
                       in. The budget stays per page lifetime; forgiveness
                       happens once, for the whole playlist, in
                       scheduleForgivingPlaylistRetry(). */
                };
                Promise.resolve(playPromise).then(playbackStarted, error => {
                    if (error?.name === 'NotAllowedError') {
                        /* Autoplay was blocked, so the next real gesture has to
                           restart it — but ARM that fallback rather than
                           assuming it is still armed. It is a one-shot, spent
                           at the player's first tap of the session, and a wake
                           refusal happens long after. Without this the music
                           waited forever for a listener that no longer existed. */
                        armInteractionResume();
                        if (window.CLIENT_DEBUG_MODE) {
                            console.log('Autoplay blocked; awaiting next user interaction');
                        }
                        return;
                    }
                    if (error?.name === 'AbortError'
                        && (attempt !== activePlaybackAttempt
                            || document.hidden
                            || !audioPlayer?.getAttribute('src'))) {
                        return;
                    }
                    handlePlaybackFailure(attempt, error);
                });
            }
        }

        function showNoSoundtracksMessage() {
            if (window.CLIENT_DEBUG_MODE) console.log('Creating no soundtracks message');
            const notice = document.createElement('div');
            notice.className = 'autoplay-notice';
            const message = document.createElement('p');
            const fallbackNotice = 'No soundtrack files found. Please add MP3, WAV, or OGG files to the soundtrack directory.';
            const localizedNotice = i18n?.t?.('audio.noSoundtracks', {});
            message.textContent = (typeof localizedNotice === 'string' && localizedNotice && localizedNotice !== 'audio.noSoundtracks')
                ? localizedNotice
                : fallbackNotice;
            notice.appendChild(message);
            document.body.appendChild(notice);

            setTimeout(() => {
                notice.style.opacity = '0';
                setTimeout(() => notice.remove(), 1000);
            }, 5000);
        }

        function nextPlayableTrackIndex() {
            if (playlist.length === 0) return -1;
            for (let offset = 1; offset <= playlist.length; offset += 1) {
                const index = (currentTrackIndex + offset) % playlist.length;
                if (!failedTracks.has(playlist[index])) return index;
            }
            return -1;
        }

        function releasePlayerMedia({
            persist = false,
            preservePendingTrackChange = false,
        } = {}) {
            releasingOwnMedia = true;
            if (persist) persistPlaybackState();
            cancelPendingTrackChange({ preserveIntent: preservePendingTrackChange });
            cancelStallWatch();
            invalidatePlaybackAttempt();
            if (!audioPlayer) return;
            try { audioPlayer.pause(); } catch (_) { /* already stopped */ }
            try {
                audioPlayer.removeAttribute('src');
                audioPlayer.srcObject = null;
            } catch (_) { /* jsdom or a locked-down element */ }
            try { audioPlayer.load(); } catch (_) { /* nothing to reset */ }
            releasingOwnMedia = false;
        }

        function runPendingTrackChange({ requireActiveAttempt = true } = {}) {
            const intent = pendingTrackIntent;
            if (!intent) return false;
            if (requireActiveAttempt && intent.attempt !== activePlaybackAttempt) {
                cancelPendingTrackChange();
                return false;
            }
            if (document.hidden) {
                suspendedByHide = true;
                cancelPendingTrackChange({ preserveIntent: true });
                return false;
            }
            cancelPendingTrackChange({ preserveIntent: true });
            pendingTrackIntent = null;
            releasePlayerMedia();
            currentTrackIndex = intent.trackIndex;
            startPlayback();
            return true;
        }

        function scheduleTrackChange(attempt, trackIndex = currentTrackIndex) {
            cancelPendingTrackChange();
            pendingTrackIntent = { attempt, trackIndex };
            pendingTrackTimer = setTimeout(() => {
                pendingTrackTimer = null;
                runPendingTrackChange();
            }, PLAYBACK_RETRY_DELAY_MS);
        }

        function handlePlaybackFailure(attempt, error) {
            if (!attempt || attempt !== activePlaybackAttempt || attempt.failureHandled) return;
            attempt.failureHandled = true;
            if (mediaErrorListener) {
                audioPlayer?.removeEventListener('error', mediaErrorListener);
                mediaErrorListener = null;
            }

            /* This engine cannot play the silence: the mute holds the track
               with nothing playing at all, and no track pays for it. */
            if (attempt.silence) {
                silenceFailed = true;
                releasePlayerMedia();
                return;
            }

            const failures = (trackFailureCounts.get(attempt.src) || 0) + 1;
            trackFailureCounts.set(attempt.src, failures);
            if (failures <= TRACK_RETRY_LIMIT) {
                scheduleTrackChange(attempt);
                return;
            }

            failedTracks.add(attempt.src);
            console.error('[Soundtrack] Track failed after a bounded retry:', attempt.src, error || 'unknown media error');
            const nextIndex = nextPlayableTrackIndex();
            if (nextIndex < 0) {
                scheduleForgivingPlaylistRetry();
                return;
            }
            scheduleTrackChange(attempt, nextIndex);
        }

        /**
         * EVERY TRACK HAS FAILED IS NOT THE END OF THE SESSION.
         *
         * This used to release the media and return, with no timer and nothing
         * left watching — so a handful of transient media errors, which is an
         * ordinary afternoon on a phone with a patchy connection, silenced the
         * music permanently. A `failedTracks` entry records that a track failed
         * a moment ago, not that it is broken forever, so once there is nothing
         * left to move to the failures are forgiven and the playlist is tried
         * again from the top.
         */
        function scheduleForgivingPlaylistRetry() {
            releasePlayerMedia();
            if (playlistExhausted) return;
            playlistExhausted = true;
            if (playlistRetryTimer !== null) clearTimeout(playlistRetryTimer);
            /* Backs off, and never gives up. A playlist that fails once is a
               network having a bad minute; a playlist that fails four times is
               a server problem — but even then, five minutes between attempts
               costs nothing and is the difference between a player whose music
               returns when the connection does and one who is silent until they
               reload the page. */
            const delay = Math.min(
                PLAYLIST_RETRY_DELAY_MS * Math.pow(4, playlistRetryAttempts),
                PLAYLIST_RETRY_MAX_DELAY_MS,
            );
            playlistRetryAttempts += 1;
            playlistRetryTimer = setTimeout(() => {
                playlistRetryTimer = null;
                playlistExhausted = false;
                trackFailureCounts.clear();
                failedTracks.clear();
                if (!playbackIntended) return;
                if (typeof document !== 'undefined' && document.hidden) {
                    suspendedByHide = true;
                    return;
                }
                currentTrackIndex = 0;
                startPlayback(getStoredPlaybackState());
            }, delay);
        }

        function playNextTrack() {
            cancelPendingTrackChange();
            invalidatePlaybackAttempt();
            const nextIndex = nextPlayableTrackIndex();
            if (nextIndex < 0) {
                scheduleForgivingPlaylistRetry();
                return;
            }
            currentTrackIndex = nextIndex;
            startPlayback();
        }

        function fadeAudioTo(targetVolume, durationMs) {
            if (!audioPlayer) return;
            // Where the gain bus owns loudness, the element's own `.volume`
            // reads back as 1 no matter what was written, so the fade must
            // start from the level we last COMMANDED, not from the element.
            const start = lastCommandedVolume;
            const safeTarget = Math.max(0, Math.min(1, Number(targetVolume) || 0));
            const safeDuration = Math.max(0, Number(durationMs) || 0);
            const steps = Math.max(1, Math.round(safeDuration / 50));
            let step = 0;

            if (fadeTimer) {
                clearInterval(fadeTimer);
                fadeTimer = null;
            }

            if (safeDuration === 0 || steps === 1) {
                setPlayerVolume(safeTarget);
                return;
            }

            fadeTimer = setInterval(() => {
                step += 1;
                const progress = Math.min(1, step / steps);
                setPlayerVolume(start + (safeTarget - start) * progress);
                if (progress >= 1) {
                    clearInterval(fadeTimer);
                    fadeTimer = null;
                }
            }, safeDuration / steps);
        }

        function clearDuck() {
            if (duckTimer) {
                clearTimeout(duckTimer);
                duckTimer = null;
            }
            if (fadeTimer) {
                clearInterval(fadeTimer);
                fadeTimer = null;
            }
            duckActive = false;
            duckFactor = null;
            duckTargetVolume = null;
        }

        function duckVolume(factor = 0.4, durationMs = 2500) {
            if (!audioPlayer || muted()) return false;
            const safeFactor = Math.max(0, Math.min(1, Number(factor) || 0.4));
            const safeDuration = Math.max(250, Number(durationMs) || 2500);

            duckActive = true;
            duckFactor = safeFactor;
            duckTargetVolume = musicVolume() * safeFactor;

            fadeAudioTo(duckTargetVolume, DUCK_FADE_MS);

            if (duckTimer) {
                clearTimeout(duckTimer);
            }

            duckTimer = setTimeout(() => {
                duckActive = false;
                duckFactor = null;
                duckTargetVolume = null;
                if (audioPlayer && !muted()) {
                    fadeAudioTo(musicVolume(), DUCK_FADE_MS);
                }
            }, safeDuration);

            return true;
        }

        function loadPlaybackHandoff() {
            pendingResumeState = getStoredPlaybackState();
        }

        function activatePlaybackHandoff() {
            document.documentElement.setAttribute('data-user-interacted', 'true');
        }

        function init() {
            if (isInitialized) return;
            isInitialized = true;

            setupPlaybackStatePersistence();
            setupBackgroundSuspend();
            /* The level moves from `.volume` to the gain node the moment the
               audio context can be heard. Without this the handoff would wait
               for the player's next volume change, and until then the element
               would be playing at a level the graph does not know about. */
            AudioGainBus?.onCarryChanged?.(onCarriageChanged);
            setupInteractionDetection();

            loadVolumePreferences();
            loadPlaybackHandoff();

            if (MoonfallCloud && MoonfallCloud.isReady && MoonfallCloud.isReady()) {
                loadPlaylist().catch((err) => {
                    console.warn('[Soundtrack] Failed to load soundtrack:', err);
                });
            } else {
                window.addEventListener('moonfall:convex-ready', () => {
                    loadPlaylist().catch((err) => {
                        console.warn('[Soundtrack] Failed to load soundtrack:', err);
                    });
                }, { once: true });
            }
        }

        function setupPlaybackStatePersistence() {
            /* Persist the position, then LET GO of the media. Pausing alone
               leaves iOS showing its Control Center now-playing widget and the
               tab's audio indicator for a game the player has left — reported
               as "game still keeps my audio playback button open... when I
               leave it". The session only ends when the element stops holding
               a loaded source, so drop it and reset the media state. The resume
               path re-assigns the src and seeks, so nothing is lost. */
            const releaseMedia = () => releasePlayerMedia({ persist: true });
            /* Leaving the page releases the media AND arms the resume flag in
               one place. It reads the INTENT rather than the element: this
               listener registers before the suspend handler's, so it used to
               depend on running while the element was still playing — and a
               platform that had already paused it (a screen lock, an incoming
               call) made both handlers read "nothing to suspend" and killed the
               resume outright. */
            const releaseOnLeave = () => {
                const preservePendingTrackChange = pendingTrackIntent !== null;
                if (playbackIntended || preservePendingTrackChange) {
                    suspendedByHide = true;
                }
                releasePlayerMedia({
                    persist: true,
                    preservePendingTrackChange,
                });
            };
            window.addEventListener('pagehide', releaseOnLeave);
            window.addEventListener('beforeunload', releaseOnLeave);

            /* Another Moonfall page taking the audio silences this one. Mobile
               browsers keep background tabs playing, so an older page can still
               be the thing the player hears while the visible page owns a
               different element — and the visible page's volume control cannot
               reach it. "It's like some old still playback ... that's why I
               think the actual volume button doesn't work, because it's not
               even the most recent playback." */
            AudioOwnership?.register?.(releaseMedia);
        }

        /**
         * A web page is not a music app. Switching tabs or leaving for another
         * app must silence Moonfall rather than leave it playing in the
         * background like a media session the player never asked for — on a
         * phone that also keeps it out of the lock screen / control centre.
         *
         * Resume is conditional on OUR state, not the element's: we only start
         * again if we paused it ourselves and the player has not muted or
         * stopped the soundtrack in the meantime.
         */
        function setupBackgroundSuspend() {
            const hide = () => {
                if (!audioPlayer) return;
                /* Read before anything here pauses or latches: the wake compares
                   this against the clock it finds, and a page that pauses its
                   own element still needs to know whether the GRAPH kept
                   running underneath. */
                graphClockAtHide = snapshotGraphClock();
                const retryPending = pendingTrackIntent !== null;
                if (audioPlayer.paused) {
                    /* ALREADY PAUSED IS NOT "NOTHING TO SUSPEND". The platform
                       can take the audio session before it tells the page it is
                       hidden — locking a phone does exactly that — so this
                       branch is also the screen lock, arriving in the other
                       order. Latch whenever this page still wanted sound, or
                       the wake below has nothing to act on and the music never
                       comes back. */
                    if (retryPending || playbackIntended) {
                        suspendedByHide = true;
                        cancelPendingTrackChange({ preserveIntent: retryPending });
                        invalidatePlaybackAttempt();
                    }
                    return;
                }
                suspendedByHide = true;
                try { audioPlayer.pause(); } catch (_) { /* nothing to pause */ }
                cancelPendingTrackChange({ preserveIntent: retryPending });
                invalidatePlaybackAttempt();
            };

            const show = () => {
                /* Spent here, before any early return: one hide is judged by
                   at most the one show that follows it, never by a later one. */
                const graphClockBeforeHide = graphClockAtHide;
                graphClockAtHide = null;
                if (!suspendedByHide) return;
                suspendedByHide = false;
                if (pendingTrackIntent !== null) {
                    runPendingTrackChange({ requireActiveAttempt: false });
                    return;
                }
                if (!audioPlayer) {
                    startPlayback();
                    return;
                }
                /* The interruption that took the audio session may also have
                   killed the graph node the music is routed through — WebKit
                   makes no promise there, and a dead node is indistinguishable
                   from a healthy one from inside the page. Rebuilding is cheap
                   and lands on the same track and time, so the wake does not
                   gamble on it. */
                if (rebuildIfCarriageWasInterrupted()) return;
                /* The bus can only report an interruption it was told about or
                   can still see. A screen lock hides both — the context is
                   `running` again by the time the page thaws, and the pair of
                   `statechange` events it queued both read `running` — so the
                   graph's own clock is asked as well. */
                if (rebuildIfGraphStoppedWhileHidden(graphClockBeforeHide)) return;
                /* Coming back to this page makes it the audio owner again. If
                   another Moonfall page took the audio while this one was
                   hidden, the release dropped the element's source, so playback
                   must be rebuilt through startPlayback (which also claims) —
                   resuming a source-less element just throws. */
                if (AudioOwnership && !AudioOwnership.holdsAudio()) {
                    startPlayback();
                    return;
                }
                if (!audioPlayer.paused) return;
                AudioOwnership?.claim?.();
                /* pagehide released the SOURCE, not just the playback. Resuming
                   a source-less element throws; rebuild from the state the
                   release persisted, which lands on the same track and time. */
                if (!audioPlayer.getAttribute('src')) {
                    startPlayback(getStoredPlaybackState());
                    return;
                }
                const attempt = beginPlaybackAttempt(playlist[currentTrackIndex]);
                tryToPlay(attempt);
            };

            document.addEventListener('visibilitychange', () => {
                if (document.hidden) hide(); else show();
            });
            // iOS fires pagehide (not always visibilitychange) when the browser
            // itself is backgrounded or the tab is swapped out.
            window.addEventListener('pagehide', hide);
            window.addEventListener('pageshow', show);
        }

        /**
         * Replace the element the graph was carrying, on the same track and at
         * the same time. Cheap, and the only honest answer to a source node
         * that may or may not have survived an interruption.
         */
        function rebuildIfCarriageWasInterrupted() {
            if (!audioPlayer) return false;
            if (!AudioGainBus?.consumeCarriageInterruption?.(audioPlayer)) return false;
            rebuildCarriage();
            return true;
        }

        /**
         * The graph stopped at some point while the page was hidden — read from
         * its clock, which is the one witness a frozen page cannot silence (see
         * GRAPH_CLOCK_GAP_MS). Only a CARRIED element is judged this way: a
         * pending one is heard directly and the graph's clock says nothing
         * about it.
         */
        function rebuildIfGraphStoppedWhileHidden(before) {
            if (!before || !audioPlayer) return false;
            if (!AudioGainBus?.carriesElement?.(audioPlayer)) return false;
            const now = AudioGainBus.describeContext?.();
            if (!now || now.generation !== before.generation) return false;
            if (typeof now.currentTime !== 'number' || typeof before.currentTime !== 'number') return false;
            const hiddenForMs = Date.now() - before.wallClock;
            const graphRanForMs = (now.currentTime - before.currentTime) * 1000;
            if (hiddenForMs - graphRanForMs < GRAPH_CLOCK_GAP_MS) return false;
            rebuildCarriage();
            return true;
        }

        function snapshotGraphClock() {
            const described = AudioGainBus?.describeContext?.();
            if (!described || typeof described.currentTime !== 'number') return null;
            return {
                generation: described.generation,
                currentTime: described.currentTime,
                wallClock: Date.now(),
            };
        }

        function rebuildCarriage() {
            releasePlayerMedia({ persist: true });
            const resumeFrom = getStoredPlaybackState();
            audioPlayer = null;
            /* A DUCK BELONGS TO THE ELEMENT IT DUCKED. Its restore is a
               setTimeout, and a page that was interrupted is exactly a page
               whose timers were throttled — so a stale duck could outlive the
               element and hand the rebuilt one a 40% ceiling with nothing left
               to lift it. */
            clearDuck();
            /* A NEW ELEMENT ON THE OLD CONTEXT IS STILL THE OLD CONTEXT. WebKit
               promises nothing about a graph that lived through an audio-session
               interruption, so the context goes too — otherwise this rebuild
               reads as a fix and changes nothing on the phone, which is exactly
               what was reported after the first attempt shipped. */
            AudioGainBus?.renewContext?.();
            startPlayback(resumeFrom);
        }

        /**
         * THE REPAIR THAT DEPENDS ON NO EVENT AT ALL.
         *
         * Every previous round of this work hung the recovery off one more
         * signal — `visibilitychange`, `pagehide`, `pageshow`, `statechange`,
         * or a one-shot gesture listener — and every one of them shipped and
         * changed nothing on the reporter's phone. The reason is in the report:
         * "the music stopped playing ... I don't think my screen went off."
         *
         * A screen lock is only one of the ways iOS takes the audio session. A
         * notification with sound, Siri, an incoming call, an alarm, another app
         * claiming audio, AirPods connecting — every one of those takes it while
         * Moonfall is still in FRONT of the player with the screen ON. WebKit
         * pauses the element itself and the page is told NOTHING: no lifecycle
         * event is fired, and the context may not even announce its own state
         * change. A recovery that waits to be told therefore never runs.
         *
         * So this asks instead of listening. INTENT is ours to know
         * (`playbackIntended`), REALITY is readable at any moment, and whenever
         * they disagree while the player is looking at the page, the channel is
         * repaired. That is also what the sound-effects channel has always had
         * for free and music never did: every SFX cue rebuilds its own path and
         * resumes the context on the way past, which is exactly why the reports
         * have always read "sfx still works but music is dead".
         *
         * What it must never do: run while the document is hidden (a web page is
         * not a music app), restart a channel that is already healthy (an
         * audible re-start would be a louder regression than the defect), undo
         * the player's own mute, or take the audio back from another Moonfall
         * page that legitimately claimed it.
         */
        /**
         * Does this element still hold a track?
         *
         * `getAttribute('src')` is what the rest of this module asks, because a
         * release drops the ATTRIBUTE rather than pausing — but `.src` answers
         * the same question on any media element (it reads back as '' once the
         * attribute is gone) and is the one form every double models. Asking
         * both keeps a health check from being the thing that throws inside a
         * player's click.
         */
        function mediaSourceOf(el) {
            if (!el) return '';
            try {
                if (typeof el.getAttribute === 'function') return el.getAttribute('src') || '';
            } catch (_) { /* a locked-down element */ }
            return el.src || '';
        }

        function musicLooksDead() {
            // A hidden page stays silent; the wake path owns that transition.
            if (typeof document !== 'undefined' && document.hidden) return false;
            if (suspendedByHide) return false;
            // A retry is already scheduled; let it land rather than racing it.
            if (pendingTrackIntent !== null) return false;
            if (releasingOwnMedia) return false;
            if (playlistExhausted) return false;
            if (playlist.length === 0) return false;
            // A mute that holds the track with nothing playing holds nothing to repair.
            if (holdingTrack && silenceFailed) return false;
            /* AN ELEMENT THAT ALREADY HOLDS A TRACK AND IS NOT PLAYING IS DEAD,
               whether or not anything has intended playback yet — that is also
               the ordinary boot, where the media is loaded and waiting for the
               autoplay policy to be satisfied by the player's first gesture.
               There is no control that stops the channel (a mute holds the
               track and keeps the element playing its silence), so a loaded,
               paused, visible channel is always something to repair — onto
               whatever it holds: the track, or the silence. */
            if (audioPlayer && mediaSourceOf(audioPlayer)) {
                if (audioPlayer.paused === true) return true;
                /* `paused === false` IS NOT "CAN BE HEARD" EITHER. Since the
                   music was routed through a MediaElementAudioSourceNode its
                   audio goes into the graph and only into the graph, so when
                   the audio session is taken and the context stops, the
                   element keeps reporting itself as playing — measured in
                   WebKit and Firefox with a routed element and a stopped
                   context: `paused` false, clock advancing, and a second
                   `play()` resolving while changing nothing, which is exactly
                   what the iOS notification bar's play button did for the
                   reporter. That one wrong answer disarmed all three lines of
                   defence at once: the gesture repair declined on every tap,
                   the `pause` event that arms the watch never fired, and the
                   stall watch cancelled itself on the advancing clock. A
                   pending element is judged by itself — it is heard directly,
                   and the bus refuses to route into a stopped context precisely
                   so that it stays audible. */
                return AudioGainBus?.carriageStalled?.(audioPlayer) === true;
            }
            /* Without an element or a source, repairing means BUILDING media —
               which must never happen on a page that has not asked for music. */
            if (!playbackIntended) return false;
            return true;
        }

        /**
         * Put the music back, from whatever state it is actually in.
         *
         * @param {{reclaim?: boolean}} options `reclaim` is only true for a real
         *   user gesture. A silent timer must never take the audio back from
         *   another Moonfall page — but a player tapping THIS page has said
         *   plainly which one they mean.
         */
        function repairMusic(options = {}) {
            /* THIS RUNS INSIDE THE PLAYER'S OWN CLICK. A repair is a
               best-effort background concern and it is now reached from a
               capture-phase listener on every gesture, so a throw here would
               propagate into an unrelated handler and break whatever the player
               was actually pressing. Nothing this function does is worth that. */
            try {
                return repairMusicUnsafe(options);
            } catch (error) {
                console.warn('[Soundtrack] Could not repair the music channel:', error);
                return false;
            }
        }

        function repairMusicUnsafe({ reclaim = false } = {}) {
            if (!musicLooksDead()) return false;
            const now = Date.now();
            if (now - lastRepairAt < REPAIR_COOLDOWN_MS) return false;

            /* THE COOLDOWN IS SPENT BY REPAIRS THAT RUN, NOT BY REPAIRS THAT
               DECLINE. It used to be stamped here, before the branch below
               decides whether there is anything to do — so the two callers that
               pass no `reclaim` (the health-watch tick and the element's own
               `pause` event) could burn it on a repair they then refused to
               perform. The player's tap is the caller that DOES reclaim, and it
               lands milliseconds after that pause event, so it was rejected by a
               rate-limiter that was protecting nothing and the music stayed dead
               until a later tap happened to fall outside the window. Each
               acting path stamps for itself instead. */

            /* The interruption that paused the element may also have killed the
               graph node it is routed through. The bus knows; ask it first,
               because a rebuild lands on the same track and time anyway. */
            if (rebuildIfCarriageWasInterrupted()) {
                lastRepairAt = now;
                return true;
            }

            if (AudioOwnership && !AudioOwnership.holdsAudio()) {
                if (!reclaim) return false;
                lastRepairAt = now;
                startPlayback(getStoredPlaybackState());
                return true;
            }

            lastRepairAt = now;
            /* A released element has no source, and resuming a source-less
               element throws — rebuild it from the state the release persisted. */
            if (!audioPlayer || !mediaSourceOf(audioPlayer)) {
                startPlayback(getStoredPlaybackState());
                return true;
            }

            AudioOwnership?.claim?.();
            /* A FRESH ATTEMPT, not `tryToPlay()` bare. The attempt this element
               last carried was invalidated by whatever stopped it, and
               tryToPlay() returns silently when handed a stale one — which is
               how a tap could be spent without starting anything. */
            const attempt = beginPlaybackAttempt(playlist[currentTrackIndex]);
            tryToPlay(attempt);
            return true;
        }

        /**
         * A WATCH THAT ONLY EXISTS WHILE SOMETHING IS WRONG.
         *
         * Armed when a defect is observed and disarmed the moment the music is
         * healthy again, so an ordinary session carries no recurring timer —
         * it costs nothing on a battery, and it does not sit in the background
         * of every other test pretending to be a pending retry.
         *
         * It is the third and last line: the element's own `pause` event is the
         * fast path, a user gesture is the path that carries the activation iOS
         * wants, and this is what covers a player who is watching an enemy turn
         * without touching the screen while no event ever arrives.
         */
        function startHealthWatch() {
            if (healthTimer !== null) return;
            healthTimer = setInterval(() => {
                if (!musicLooksDead()) {
                    stopHealthWatch();
                    return;
                }
                /* Another Moonfall page holds the audio. That is not a defect
                   and a timer must never take it back — only the player tapping
                   THIS page says which one they mean, and that path reclaims.
                   Stop rather than spin. */
                if (AudioOwnership && !AudioOwnership.holdsAudio()) {
                    stopHealthWatch();
                    return;
                }
                repairMusic();
            }, HEALTH_INTERVAL_MS);
        }

        function stopHealthWatch() {
            if (healthTimer === null) return;
            clearInterval(healthTimer);
            healthTimer = null;
        }

        /**
         * The element stopped. Ours, or the platform's?
         *
         * `pause` is the one signal a foreground audio-session takeover DOES
         * leave behind — WebKit pauses the element and the HTML pause steps fire
         * this — and nothing in this module has ever listened to it. Every
         * deliberate stop this module performs is already excluded by
         * `musicLooksDead()` (a hide latches `suspendedByHide`, a track change
         * leaves `pendingTrackIntent`, an eviction drops ownership), so what
         * reaches the repair is a stop nobody here asked for.
         */
        /**
         * `paused === false` IS NOT `PLAYING`.
         *
         * A mid-track buffer underrun on a phone leaves the element reporting
         * itself as playing with `currentTime` frozen, and `ended` never fires
         * — so the track never advances and no watcher in this module was
         * looking. The element announces this itself through `waiting`/`stalled`
         * and usually recovers on its own within a second or two, so the event
         * alone is not evidence of anything. Time is: if the element still
         * claims to be playing and has made NO progress after several seconds,
         * it is not playing.
         */
        function cancelStallWatch() {
            if (stallWatchTimer !== null) {
                clearTimeout(stallWatchTimer);
                stallWatchTimer = null;
            }
            stallWatchFromTime = null;
        }

        function bufferedEndOf(el) {
            try {
                const ranges = el?.buffered;
                let end = 0;
                for (let i = 0; i < (ranges?.length || 0); i += 1) end = Math.max(end, Number(ranges.end(i)) || 0);
                return end;
            } catch (_) {
                return 0;
            }
        }

        function onElementBytes() {
            lastBytesAt = Date.now();
        }

        function onElementStalled() {
            if (!audioPlayer || stallWatchTimer !== null) return;
            stallWatchFromTime = Number(audioPlayer.currentTime) || 0;
            watchStallFor(STALL_CONFIRM_MS);
        }

        function watchStallFor(ms) {
            stallWatchWindowStart = Date.now();
            stallWatchBufferedEnd = bufferedEndOf(audioPlayer);
            stallWatchTimer = setTimeout(checkStall, ms);
        }

        function checkStall() {
            stallWatchTimer = null;
            const el = audioPlayer;
            if (!el) return;
            if (typeof document !== 'undefined' && document.hidden) return;
            if (el.paused) return;                     // the pause path owns it
            const now = Number(el.currentTime) || 0;
            if (now !== stallWatchFromTime) return;    // it recovered by itself
            /* SLOW IS NOT STALLED. On 1 Mbps a track waits at 0:00 for its
               first seconds of buffer far longer than the window, and skipping
               it threw its bytes away and started the next track from zero:
               six tracks in 45 s, none of them heard (28 September 2026). Bytes
               still arriving — `progress`, or a buffer that grew where an
               engine is sparing with the event — keep the track; only a whole
               window with nothing arriving skips it. */
            if (bufferedEndOf(el) > stallWatchBufferedEnd && lastBytesAt <= stallWatchWindowStart) {
                lastBytesAt = Date.now();
            }
            const quietFor = Date.now() - lastBytesAt;
            if (quietFor < STALL_CONFIRM_MS) {
                watchStallFor(STALL_CONFIRM_MS - quietFor);
                return;
            }
            /* Genuinely wedged: nothing arrived for the whole window. Move on
               rather than sitting in silence — the same source may simply be
               unreachable right now. */
            console.warn('[Soundtrack] A track stalled without recovering; advancing.');
            playNextTrack();
        }

        function onElementProgress() {
            cancelStallWatch();
            /* THE ELEMENT'S OWN HEARTBEAT IS THE ONE EVENT A SILENT CARRIAGE
               STILL DELIVERS. A routed element whose graph has stopped fires
               no `pause` — it keeps ticking `timeupdate` into a graph nobody
               can hear — so this is where the watch gets armed for it. Armed
               only, not repaired inline: the first tick is a few seconds away,
               and that is the grace the platform gets to end its own
               interruption (WebKit hands the session back by itself when it
               can) before the channel throws the context away and rebuilds. A
               healthy channel pays one cheap read per heartbeat and arms
               nothing. */
            if (musicLooksDead()) startHealthWatch();
        }

        function onElementPaused() {
            if (!musicLooksDead()) return;
            /* Not repaired inline: the pause may be one step of a sequence this
               module is in the middle of, and a task boundary lets that finish
               before anything is judged broken. */
            startHealthWatch();
            repairMusic();
        }

        function onCarriageChanged() {
            if (rebuildIfCarriageWasInterrupted()) return;
            applyVolumeToPlayer();
        }

        const INTERACTION_EVENTS = ['click', 'touchstart', 'keydown', 'mousedown'];

        function markAsInteracted() {
            document.documentElement.setAttribute('data-user-interacted', 'true');

            repairMusic({ reclaim: true });

            /* SPEND THE ONE-SHOT ONLY ONCE IT HAS ACHIEVED SOMETHING.
               This used to remove all four listeners unconditionally, and the
               call above used to be a bare `tryToPlay()` — which returns
               silently when the attempt it reads has already been invalidated.
               So a tap that started nothing still disarmed the fallback, and the
               music waited for a listener that no longer existed. */
            if (audioPlayer && audioPlayer.paused === false) {
                INTERACTION_EVENTS.forEach(event => {
                    document.removeEventListener(event, markAsInteracted, true);
                });
            }
        }

        /**
         * Arm (or re-arm) the "next real gesture restarts the music" fallback.
         *
         * Idempotent: re-adding an identical listener is a no-op, so callers
         * never need to track whether it is currently armed.
         *
         * This exists because the original was armed ONCE, at boot, and
         * markAsInteracted() removed all four listeners the first time it
         * fired. Measured on a real iPhone (iOS 26.4.2): locking the screen
         * mid-match fires only `visibilitychange` — no pagehide/pageshow — so
         * show() calls tryToPlay(), iOS rejects play() with NotAllowedError
         * because a wake carries no user activation, and the catch below then
         * "silently waited" for a handler that had been removed minutes
         * earlier. The music never came back for the rest of the session while
         * SFX kept working, exactly as reported.
         */
        function armInteractionResume() {
            /* CAPTURE, because the gesture that revives the music lands on the
               game board, and the board owns the pointer: a handler between the
               target and `document` that calls stopPropagation() deletes the
               only path back to sound. AudioGainBus already arms its own
               gesture resume in the capture phase for exactly this reason;
               this half was still listening in the bubble phase. */
            INTERACTION_EVENTS.forEach(event => {
                document.addEventListener(event, markAsInteracted, true);
            });
        }

        /**
         * A gesture repair that is NEVER spent.
         *
         * The listener above is a one-shot by design — it is the autoplay
         * fallback, and its job ends when the music first starts. But an
         * interruption arrives in the middle of a match, long after the player's
         * first tap, so by then there is nothing armed at all. That is the
         * reported "no later tap can revive it".
         *
         * This one stays for the life of the page. It costs an early return on
         * a healthy channel, which is what it does on essentially every tap, and
         * it is the fastest path back to sound the moment one is needed: a tap
         * is the one thing that carries the user activation iOS demands before
         * it will hand an interrupted audio session back.
         */
        const REPAIR_GESTURES = ['pointerdown', 'touchend', 'keydown'];

        function onRepairGesture() {
            repairMusic({ reclaim: true });
        }

        function setupInteractionDetection() {
            armInteractionResume();
            REPAIR_GESTURES.forEach(event => {
                document.addEventListener(event, onRepairGesture, { capture: true, passive: true });
            });
        }

        document.addEventListener('DOMContentLoaded', init);

        return {
            init,
            toggleMute,
            playNextTrack,

            setVolume(value) {
                setMusicVolumeInternal(value);
            },
            getVolume() {
                return volume;
            },
            setMusicVolume(value) {
                setMusicVolumeInternal(value);
            },
            getMusicVolume() {
                return musicVolume();
            },
            isMuted: muted,
            duckVolume,
        };
})();

Soundtrack.init();

export { Soundtrack };
